import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { standardGetHttpOptions, standardHttpOptions } from '@models';
import { LoadingService } from '@services/loading.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { KigSettings, KIG_SETTINGS } from '../../../settings/kig-settings';
import { MapFavoriteInfo } from '@models/MapFavorite/mapFavoriteInfo.model';
import { MapFavoriteRequest } from '@models/MapFavorite/mapFavoriteRequest.model';
import { MapLabelInfo } from '@models/MapFavorite/mapLabelInfo.model';
import { MapLabelRequest } from '@models/MapFavorite/mapLabelRequest.model';
import { ImageLabelValue } from '@models/ImageFavorite/imageLabelValue.model';


@Injectable()
export class MapFavoriteService {
  public standardHttpOptionsWithUser: any;

  private apiUrl: string;

  constructor(private http: HttpClient,
              private loadingService: LoadingService,
              @Inject(KIG_SETTINGS) private settings: KigSettings) {
    this.apiUrl = this.settings.apiUrl;
  }


  buildStandardHttpOptionsWithUser(username: string) {
    this.standardHttpOptionsWithUser = {
      withCredentials: true,
      headers: new HttpHeaders({
        ['Content-Type']: 'application/json',
        username
      })
    };
  }


  // ----- Map favorites -----

  public getMapFavorites(): Observable<MapFavoriteInfo[]> {
    this.loadingService.setMessage('Loading map favorites ...');
    return this.http.get<MapFavoriteInfo[]>(`${this.apiUrl}/mapfavorite`, standardGetHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  // Resolves to the new favorite's numeric id on success.
  public createMapFavorite(mapFavoriteRequest: MapFavoriteRequest): Observable<any> {
    this.loadingService.setMessage('Creating map favorite ...');
    return this.http.post<number>(`${this.apiUrl}/mapfavorite`, mapFavoriteRequest, standardHttpOptions).pipe(
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public updateMapFavorite(mapFavoriteRequest: MapFavoriteRequest, username: string): Observable<any> {
    this.loadingService.setMessage('Updating map favorite ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${this.apiUrl}/mapfavorite`, mapFavoriteRequest, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public deleteMapFavorite(mapFavoriteId: number, username: string): Observable<any> {
    this.loadingService.setMessage('Deleting map favorite ...');
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${this.apiUrl}/mapfavorite/${mapFavoriteId}`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError),
      finalize(() => this.loadingService.clearMessage()));
  }


  public getLabelValues(mapFavoriteId: number): Observable<ImageLabelValue[]> {
    return this.http.get<ImageLabelValue[]>(`${this.apiUrl}/mapfavorite/${mapFavoriteId}/labelvalues`, standardGetHttpOptions).pipe(
      catchError(this.handleError));
  }


  // Values for unsaved dynamic labels (dashboard wizard preview); ids may be temporary.
  public getPreviewLabelValues(labels: MapLabelRequest[]): Observable<ImageLabelValue[]> {
    if (labels.length === 0) {
      return of([]);
    }
    return this.http.post<ImageLabelValue[]>(`${this.apiUrl}/mapfavorite/previewlabelvalues`, labels, standardHttpOptions).pipe(
      catchError(this.handleError));
  }


  // ----- Map labels -----

  public getMapLabels(mapFavoriteId: number): Observable<MapLabelInfo[]> {
    return this.http.get<MapLabelInfo[]>(`${this.apiUrl}/maplabel/forfavorite/${mapFavoriteId}`, standardGetHttpOptions).pipe(
      catchError(this.handleError));
  }


  // Resolves to the new label's numeric id on success.
  public createMapLabel(mapLabelRequest: MapLabelRequest): Observable<any> {
    return this.http.post<number>(`${this.apiUrl}/maplabel`, mapLabelRequest, standardHttpOptions).pipe(
      catchError(this.handleError));
  }


  public updateMapLabel(mapLabelRequest: MapLabelRequest, username: string): Observable<any> {
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.put(`${this.apiUrl}/maplabel`, mapLabelRequest, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError));
  }


  public deleteMapLabel(mapLabelId: number, username: string): Observable<any> {
    this.buildStandardHttpOptionsWithUser(username);
    return this.http.delete(`${this.apiUrl}/maplabel/${mapLabelId}`, this.standardHttpOptionsWithUser).pipe(
      map((response: any) => response == null ? 'success' : response),
      catchError(this.handleError));
  }


  private handleError(error: any) {
    if (error.status === 404) {
      return of(null);
    }
    return throwError(() => error);
  }
}
